import type { ReactNode } from "react";

import { monolithMoment } from "@/utils/date-format";

/**
 * The fields LatestCocSection renders. Both evidence CocRecord and
 * StorageCocRecord satisfy this shape.
 */
export interface CocTooltipRecord {
  coc_id: number;
  timestamp: string;
  type: string;
  custody_from: string | null;
  custody_to: string | null;
  reason: string | null;
}

interface LatestCocSectionProps {
  latest: CocTooltipRecord | null;
  isPending: boolean;
  isError: boolean;
  /**
   * Rendered after the record rows inside a divider-topped `.footer` block,
   * only when a record is shown.
   */
  footer?: ReactNode;
}

/**
 * "Most recent chain of custody" section of the location hover tooltips:
 * loading / error / empty lines, or the latest record's rows. Must be
 * rendered inside TooltipBody.
 */
const LatestCocSection: React.FC<LatestCocSectionProps> = ({
  latest,
  isPending,
  isError,
  footer,
}) => {
  if (isPending) return <div className="muted">Loading...</div>;
  if (isError) {
    return <div className="muted">Could not load chain of custody records</div>;
  }
  if (!latest) {
    return <div className="muted">No chain of custody records available</div>;
  }

  return (
    <>
      <div className="tooltip-title">Most recent chain of custody</div>
      <div className="row">
        <span className="label">Type</span>
        <span className="value">{latest.type}</span>
      </div>
      <div className="row">
        <span className="label">Timestamp</span>
        <span className="value">
          {monolithMoment({
            timestamp: latest.timestamp,
            includeTime: true,
          })}
        </span>
      </div>
      <div className="row">
        <span className="label">Custody From</span>
        <span className="value">{latest.custody_from}</span>
      </div>
      <div className="row">
        <span className="label">Custody To</span>
        <span className="value">{latest.custody_to}</span>
      </div>
      <div className="row">
        <span className="label">Reason</span>
        <span className="value">{latest.reason}</span>
      </div>
      {footer && <div className="footer">{footer}</div>}
    </>
  );
};

export default LatestCocSection;
